import React from "react";
import DOMPurify from "dompurify";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { wrapMsgId, uniq, normalizeReSubject, type ReplyPayload } from "./mailReply.helpers";
import { useGetTicketMailsQuery, useSendMailReplyMutation } from "@/api/ticketApi/ticket";
import { useTranslation } from "react-i18next";

type PaginationParams = { page: number; pageSize: number };
type QueryParams = { filters: Record<string, any>; pagination: PaginationParams };

type MailRecipient = {
    mail_recipient_id: number;
    mail_id: number;
    type: "to" | "cc" | "bcc" | null;
    address: string | null;
    name: string | null;
};

type MailAttachment = {
    attachment_id: number;
    mail_id: number;
    filename: string | null;
    mime_type: string | null;
    size_bytes: number | null;
    storage_url: string | null;
    content_id: string | null;
    is_inline: boolean | null;
};

type MailItem = {
    mail_id: number;
    ticket_id: number;
    direction: "inbound" | "outbound" | null;
    actor_type: "client" | "agent" | null;

    subject: string | null;
    text_body: string | null;
    html_body: string | null;
    from_address: string | null;
    reply_to_address: string | null;

    message_id: string | null;
    references: string[] | null;

    sent_at: string | null;
    received_at: string | null;

    status: string | null;

    recipients: MailRecipient[];
    attachments: MailAttachment[];

    headers?: Record<string, any> | null;

    client?: { client_email?: string | null; client_username?: string | null } | null;
    agent?: { agent_email?: string | null; agent_username?: string | null; display_name?: string | null } | null;

    createdAt?: string;
};

type TicketMailsResponse = {
    ticket_id: number;
    ticket_ref: string | null;
    count: number;
    limit: number;
    offset: number;
    items: MailItem[];
};

function fmt(iso?: string | null) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
}

function toLine(recs: MailRecipient[]) {
    const to = recs
        .filter(r => (r.type || "").toLowerCase() === "to")
        .map(r => r.address)
        .filter(Boolean) as string[];
    return to.length ? to.join(", ") : "—";
}

function statusBadge(status?: string | null) {
    const s = (status || "").toLowerCase();
    if (s === "received") return "bg-slate-100 text-slate-800 border-slate-200";
    if (s === "delivered") return "bg-green-100 text-green-800 border-green-200";
    if (s === "sent") return "bg-blue-100 text-blue-800 border-blue-200";
    if (s === "failed") return "bg-red-100 text-red-800 border-red-200";
    if (s === "queued") return "bg-amber-100 text-amber-800 border-amber-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
}

function directionBadge(direction?: string | null) {
    if (direction === "inbound") return "bg-indigo-50 text-indigo-700 border-indigo-200";
    if (direction === "outbound") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-gray-50 text-gray-600 border-gray-200";
}

function preview(m: MailItem) {
    const t = (m.text_body || "").trim();
    if (t) return t.slice(0, 120);
    const h = (m.html_body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return h.slice(0, 120);
}

function escapeHtml(s: string) {
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function textToHtml(text: string) {
    const safe = escapeHtml(text || "");
    return `<div>${safe.replace(/\n/g, "<br/>")}</div>`;
}

/* ===================== HEADER HELPERS ===================== */

function readHeader(m: MailItem | null, key: string): string | null {
    const h = m?.headers;
    if (!h || typeof h !== "object") return null;
    const k = Object.keys(h).find(x => x.toLowerCase() === key.toLowerCase());
    const v = k ? (h as any)[k] : null;
    return typeof v === "string" && v.trim() ? v.trim() : null;
}

function firstInboundOf(items: MailItem[]) {
    return items.find(m => m.direction === "inbound") ?? null;
}

function pickSupportAddr(firstInbound: MailItem | null) {
    const hdrTo = readHeader(firstInbound, "To");
    if (hdrTo) return hdrTo;

    const recTo = toLine(firstInbound?.recipients || []);
    if (recTo !== "—") return recTo;

    return "";
}

function pickUiFrom(selected: MailItem | null, firstInbound: MailItem | null, supportAddr: string) {
    if (!selected) return "—";

    if (selected.direction === "inbound") {
        return (
            selected.from_address?.trim() ||
            readHeader(selected, "From") ||
            selected.client?.client_email?.trim() ||
            "—"
        );
    }

    return supportAddr || selected.agent?.agent_email?.trim() || "—";
}

function pickUiTo(selected: MailItem | null, firstInbound: MailItem | null, supportAddr: string) {
    if (!selected) return "—";

    if (selected.direction === "inbound") {
        const recLine = toLine(selected.recipients || []);
        if (recLine !== "—") return recLine;
        return readHeader(selected, "To") || supportAddr || "—";
    }

    const recLine = toLine(selected.recipients || []);
    if (recLine !== "—") return recLine;

    return selected.client?.client_email?.trim() || readHeader(firstInbound, "From") || "—";
}

/* ===================== COMPONENT ===================== */

export default function TicketMailTab(props: {
    ticketId: string;
    currentAgentName?: string;
}) {
    const { ticketId, currentAgentName = "Agent" } = props;
     const { t } = useTranslation("ticket");
  const { t: tCommon } = useTranslation("common");

    const [filters, setFilters] = React.useState({ direction: "all", status: "all", q: "" });
    const [pagination, setPagination] = React.useState<PaginationParams>({ page: 1, pageSize: 25 });

    // params hâlâ UI filtreleme/pagination için kullanılacak (client-side)
    const params: QueryParams = React.useMemo(
        () => ({
            filters: {
                direction: filters.direction !== "all" ? filters.direction : undefined,
                status: filters.status !== "all" ? filters.status : undefined,
                q: filters.q.trim() ? filters.q.trim() : undefined,
            },
            pagination,
        }),
        [filters, pagination]
    );

    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useGetTicketMailsQuery(ticketId);

    const resp = data as TicketMailsResponse | undefined;
    const items = resp?.items ?? [];

    const firstInbound = React.useMemo(() => firstInboundOf(items), [items]);
    const supportAddr = React.useMemo(() => pickSupportAddr(firstInbound), [firstInbound]);

    const filtered = React.useMemo(() => {
        const q = filters.q.trim().toLowerCase();
        const base = items.filter(m => {
            if (filters.direction !== "all" && m.direction !== filters.direction) return false;
            if (filters.status !== "all" && (m.status || "") !== filters.status) return false;
            if (!q) return true;
            const hay = [
                m.subject,
                m.text_body,
                m.from_address,
                m.client?.client_email,
                m.client?.client_username,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        });

        // ✅ client-side pagination
        const start = (pagination.page - 1) * pagination.pageSize;
        return base.slice(start, start + pagination.pageSize);
    }, [items, filters, pagination.page, pagination.pageSize]);

    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const selected = React.useMemo(() => {
        if (!filtered.length) return null;
        const pick = selectedId ? filtered.find(m => m.mail_id === selectedId) : null;
        return pick ?? filtered[0];
    }, [filtered, selectedId]);

    const uiFrom = React.useMemo(
        () => pickUiFrom(selected, firstInbound, supportAddr),
        [selected, firstInbound, supportAddr]
    );

    const uiTo = React.useMemo(
        () => pickUiTo(selected, firstInbound, supportAddr),
        [selected, firstInbound, supportAddr]
    );

    // Reply draft
    const [draftText, setDraftText] = React.useState("");
    const [draftHtml, setDraftHtml] = React.useState("");

    const [sendReply, sendReplyState] = useSendMailReplyMutation();
    const canSend = Boolean(selected && draftText.trim());

    async function onSend() {
        if (!selected) return;

        const recipient =
            selected.reply_to_address?.trim() ||
            selected.from_address?.trim() ||
            readHeader(selected, "From") ||
            readHeader(firstInbound, "From") ||
            selected.client?.client_email?.trim() ||
            "";

        if (!recipient) {
            alert(t("ticketMailPanel.emailNotDeterminedAlert"));
            return;
        }

        const replyTo = wrapMsgId(selected.message_id);
        if (!replyTo) {
            alert(t("ticketMailPanel.noMessageIdAlert"));
            return;
        }

        const refs = uniq([...(selected.references || []).map(wrapMsgId), replyTo]);

        const ticketRef = resp?.ticket_ref;
        const ticketIdFinal = ticketRef ? ticketRef : String(ticketId);
        const htmlFinal = draftHtml?.trim() ? draftHtml.trim() : textToHtml(draftText);

        const payload: ReplyPayload = {
            ticket_id: ticketIdFinal,
            recipient_email: recipient,
            agent_name: currentAgentName,
            original_subject: normalizeReSubject(selected.subject),
            reply_to_message_id: replyTo,
            references: refs,
            email_body_text: draftText,
            email_body_html: htmlFinal,
            attachments: [],
        };

        try {
            await sendReply(payload).unwrap();
            setDraftText("");
            setDraftHtml("");
            refetch?.();
        } catch (e: any) {
            alert(e?.data?.message || e?.message || t("ticketMailPanel.replyFailed"));
        }
    }

    return (
        <div className="w-full">
            <div className="rounded-xl border bg-white">
                {/* top bar */}
                <div className="p-4 border-b flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-base font-medium"> {t("ticketMailPanel.mails")}</div>
                            <div className="text-xs text-muted-foreground">
                                ticket: {resp?.ticket_ref ?? ticketId} • {isLoading ? tCommon("loading") : `${items.length} message(s)`}
                            </div>
                        </div>

                        <Button className="h-9 px-3 text-sm border bg-white hover:bg-slate-50"
                            onClick={() => refetch?.()}>
                            {tCommon("refresh")}
                        </Button>
                    </div>

                    {/* filters */}
                    <div className="flex flex-wrap gap-2 items-center">
                        <select
                            className="h-9 rounded-md border px-2 text-sm"
                            value={filters.direction}
                            onChange={(e) => setFilters(f => ({ ...f, direction: e.target.value }))}
                        >
                            <option value="all">{t("ticketMailPanel.filtersDirection.all")}</option>
                            <option value="inbound">{t("ticketMailPanel.filtersDirection.inbound")}</option>
                            <option value="outbound">{t("ticketMailPanel.filtersDirection.outbound")}</option>
                        </select>

                        <select
                            className="h-9 rounded-md border px-2 text-sm"
                            value={filters.status}
                            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                        >
                            <option value="all">{t("ticketMailPanel.filtersStatus.all")}</option>
                            <option value="received">{t("ticketMailPanel.filtersStatus.received")}</option>
                            <option value="queued">{t("ticketMailPanel.filtersStatus.queued")}</option>
                            <option value="sent">{t("ticketMailPanel.filtersStatus.sent")}</option>
                            <option value="delivered">{t("ticketMailPanel.filtersStatus.delivered")}</option>
                            <option value="failed">{t("ticketMailPanel.filtersStatus.failed")}</option>
                        </select>

                        <input
                            className="h-9 flex-1 min-w-[220px] rounded-md border px-3 text-sm"
                            placeholder="Search subject/body/sender…"
                            value={filters.q}
                            onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
                        />

                        <div className="ml-auto flex items-center gap-2">
                            <Button
                                className="h-9 px-3 text-sm border bg-white hover:bg-slate-50"

                                disabled={pagination.page <= 1}
                                onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                            >
                                {tCommon("prev")}
                            </Button>

                            <span className="text-sm tabular-nums">{pagination.page}</span>

                            <Button
                                className="h-9 px-3 text-sm border bg-white hover:bg-slate-50"

                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                            >
                                {tCommon("next")}
                            </Button>

                            <select
                                className="h-9 rounded-md border px-2 text-sm"
                                value={pagination.pageSize}
                                onChange={(e) => setPagination({ page: 1, pageSize: Number(e.target.value) })}
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}/{tCommon("perPage")}</option>)}
                            </select>
                        </div>
                    </div>

                    {isError && (
                        <div className="text-sm text-red-600">
                            {(error as any)?.data?.message ?? (error as any)?.message ?? t("ticketMailPanel.loadFailed")}
                        </div>
                    )}
                </div>

                {/* content */}
                <div className="grid grid-cols-12 min-h-[520px]">
                    {/* list */}
                    <div className="col-span-12 md:col-span-5 lg:col-span-4 border-r">
                        {isLoading ? (
                            <div className="p-4 text-sm text-muted-foreground">{tCommon("loading")}</div>
                        ) : filtered.length === 0 ? (
                            <div className="p-6 text-sm text-muted-foreground">{tCommon("noMails")}</div>
                        ) : (
                            <div className="max-h-[520px] overflow-auto">
                                {filtered.map(m => {
                                    const active = selected?.mail_id === m.mail_id;
                                    return (
                                        <button
                                            key={m.mail_id}
                                            onClick={() => setSelectedId(m.mail_id)}
                                            className={cn(
                                                "w-full text-left p-4 border-b hover:bg-slate-50 transition",
                                                active && "bg-slate-50"
                                            )}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium truncate">{m.subject || "(no subject)"}</div>
                                                    <div className="text-xs text-muted-foreground truncate mt-1">
                                                        {m.client?.client_username || m.from_address || "—"}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1 shrink-0">
                                                    <Badge className={cn("text-[11px] border", directionBadge(m.direction))}>
                                                        {m.direction ?? "—"}
                                                    </Badge>

                                                    <Badge className={cn("text-[11px] border", statusBadge(m.status))}>
                                                        {m.status ?? "—"}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="text-xs text-muted-foreground mt-2 line-clamp-2">
                                                {preview(m) || "—"}
                                            </div>

                                            <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                                                <div className="truncate">{tCommon("to")}: {toLine(m.recipients || [])}</div>
                                                <div className="tabular-nums">{fmt(m.received_at || m.sent_at || m.createdAt)}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* detail + reply */}
                    <div className="col-span-12 md:col-span-7 lg:col-span-8 flex flex-col">
                        {/* detail header */}
                        <div className="p-5 border-b">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-lg font-medium truncate">{selected?.subject || "Select a mail"}</div>

                                    <div className="text-sm text-muted-foreground mt-1">
                                        <span className="font-medium text-foreground">{tCommon("from")}:</span>{" "}
                                        {uiFrom}
                                    </div>

                                    <div className="text-sm text-muted-foreground">
                                        <span className="font-medium text-foreground">{tCommon("to")}:</span>{" "}
                                        {uiTo}
                                    </div>
                                </div>

                                <div className="flex flex-col items-end gap-2 shrink-0">
                                    <Badge className={cn("text-[11px] border", directionBadge(selected?.direction ?? null))}>
                                        {selected?.direction ?? "—"}
                                    </Badge>
                                    <Badge className={cn("text-[11px] border", statusBadge(selected?.status ?? null))}>
                                        {selected?.status ?? "—"}
                                    </Badge>
                                    <div className="text-xs text-muted-foreground tabular-nums">
                                        {fmt(selected?.received_at || selected?.sent_at || selected?.createdAt)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* body */}
                        <div className="flex-1 min-h-0 overflow-auto p-5">
                            {!selected ? (
                                <div className="text-sm text-muted-foreground">{t("ticketMailPanel.selectMessage")}</div>
                            ) : selected.text_body?.trim() ? (
                                <pre className="whitespace-pre-wrap text-sm leading-6">{selected.text_body}</pre>
                            ) : selected.html_body ? (
                                <div
                                    className="prose max-w-none"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.html_body) }}
                                />
                            ) : (
                                <div className="text-sm text-muted-foreground">{t("ticketMailPanel.noBody")}</div>
                            )}
                        </div>

                        {/* reply composer */}
                        <div className="border-t bg-slate-50 p-4">
                            <div className="flex items-start gap-3">
                                <div className="flex-1">
                                    <div className="text-sm font-medium">{tCommon("reply")}</div>

                                    <textarea
                                        className="mt-2 w-full min-h-[110px] rounded-md border bg-white px-3 py-2 text-sm"
                                        placeholder={selected ? t("ticketMailPanel.writeReply") : t("ticketMailPanel.selectMailFirst")}
                                        value={draftText}
                                        onChange={(e) => setDraftText(e.target.value)}
                                        disabled={!selected || sendReplyState.isLoading}
                                    />

                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-xs text-muted-foreground">
                                            {t("ticketMailPanel.htmlBody")}
                                        </summary>
                                        <textarea
                                            className="mt-2 w-full min-h-[80px] rounded-md border bg-white px-3 py-2 text-sm"
                                            placeholder="<p>…</p>"
                                            value={draftHtml}
                                            onChange={(e) => setDraftHtml(e.target.value)}
                                            disabled={!selected || sendReplyState.isLoading}
                                        />
                                    </details>

                                    {sendReplyState.isError && (
                                        <div className="mt-2 text-sm text-red-600">
                                            {(sendReplyState.error as any)?.data?.message ??
                                                (sendReplyState.error as any)?.message ??
                                                t("ticketMailPanel.failedToSendReply")}
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <Button onClick={onSend} disabled={!canSend || sendReplyState.isLoading}>
                                        {sendReplyState.isLoading ? tCommon("sending") : tCommon("send")}
                                    </Button>

                                    <Button className="h-9 px-3 text-sm border bg-white hover:bg-slate-50" disabled>
                                        {tCommon("attach")}
                                    </Button>
                                </div>
                            </div>

                            {selected && (
                                <div className="mt-3 text-[11px] text-muted-foreground">
                                    {tCommon("replyTo")}:{" "}
                                    {selected.reply_to_address ||
                                        selected.from_address ||
                                        readHeader(selected, "From") ||
                                        readHeader(firstInbound, "From") ||
                                        selected.client?.client_email ||
                                        "—"}
                                    {" • "}
                                    {tCommon("inReplyTo")}:{" "} {wrapMsgId(selected.message_id) || "—"}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
